import React, { useState, useEffect } from 'react';
import Plot from 'react-plotly.js';
import NotifSvg from '../assets/svg/notif.svg';
import History from './history';
import CHHistory from './ch-history';
import Auth from './auth';
import Add from './add';
import { displayData } from '../api/data';
import * as XLSX from 'xlsx';

const ParameterTrends = () => {
  const [parameter, setParameter] = useState('overallTemp');
  const [range, setRange] = useState('daily');
  const [chartData, setChartData] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isHistory, setIsHistory] = useState(false);
  const [isCHHistory, setIsCHHistory] = useState(false);
  const [isAuth, setIsAuth] = useState(false);
  const [isAdd, setIsAdd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showDateInputs, setShowDateInputs] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      let startDateFilter = startDate;
      let endDateFilter = endDate;
      let filterType = range;

      const formatDate = (date) => {
        if (!date) return '';
        const d = new Date(date);
        return d.toISOString().slice(0, 19).replace('T', ' ');
      };

      if (range === 'custom') {
        if (!startDate || !endDate) {
          setLoading(false);
          return;
        }
        filterType = 'custom';
      } else if (filterType === '3_hours' || filterType === '6_hours') {
        const now = new Date();
        endDateFilter = formatDate(now);
        const hoursAgo = filterType === '3_hours' ? 3 : 6;
        const startTime = new Date(now.getTime() - hoursAgo * 60 * 60 * 1000);
        startDateFilter = formatDate(startTime);
      } else if (filterType === 'daily') {
        const today = new Date();
        startDateFilter = formatDate(new Date(today.setHours(0, 0, 0, 0)));
        endDateFilter = formatDate(new Date(today.setHours(23, 59, 59, 999)));
      } else if (filterType === 'weekly') {
        const today = new Date();
        const dayOfWeek = today.getDay();
        const diff = today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
        const weekStart = new Date(today.setDate(diff));
        startDateFilter = formatDate(new Date(weekStart.setHours(0, 0, 0, 0)));
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        endDateFilter = formatDate(new Date(weekEnd.setHours(23, 59, 59, 999)));
      } else if (filterType === 'monthly') {
        const today = new Date();
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        startDateFilter = formatDate(monthStart);
        const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        endDateFilter = formatDate(new Date(monthEnd.setHours(23, 59, 59, 999)));
      } else if (filterType === 'yearly') {
        const today = new Date();
        const yearStart = new Date(today.getFullYear(), 0, 1);
        startDateFilter = formatDate(yearStart);
        const yearEnd = new Date(today.getFullYear(), 11, 31);
        endDateFilter = formatDate(new Date(yearEnd.setHours(23, 59, 59, 999)));
      }

      const params = { 
        filter: filterType, 
        param: parameter === 'overallTemp' ? undefined : parameter,
        start_date: startDateFilter,
        end_date: endDateFilter 
      };

      if (parameter === 'overallTemp') {
        const paramList = ['reservoirTemp', 'pitcherTemp', 'peltierTemp'];
        const responses = await Promise.all(
          paramList.map(async (param) => {
            const res = await displayData({ ...params, param });
            return res.status === 'success' && res.data.length > 0
              ? { param, data: res.data }
              : { param, data: [] };
          })
        );

        const formattedData = responses.map(({ param, data }) => ({
          name: param.replace('Temp', ' Temperature'),
          x: data.map((item) => (filterType === 'every_3_hours' && item.time_group ? item.time_group : item.created_at)),
          y: data.map((item) => parseFloat(item[param]) || 0),
        }));

        setChartData(formattedData);
      } else {
        const response = await displayData(params);

        if (response.status === 'success' && response.data.length > 0) {
          setChartData([
            {
              name: parameter.replace('Temp', ' Temperature'),
              x: response.data.map((item) => (filterType === 'every_3_hours' && item.time_group ? item.time_group : item.created_at)),
              y: response.data.map((item) => parseFloat(item[parameter]) || 0),
            },
          ]);
        } else {
          setChartData([]);
        }
      }
    } catch (error) {
      console.error('Fetch error:', error);
      setChartData([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 300000);
    return () => clearInterval(interval);
  }, [parameter, range, startDate, endDate]);

  const handleRangeChange = (e) => {
    const value = e.target.value;
    setRange(value);
    setShowDateInputs(value === 'custom');
    if (value !== 'custom') {
      fetchData();
    }
  };

  const handleAddClick = () => {
    localStorage.getItem('user') ? setIsAdd(true) : setIsAuth(true);
  };

  const exportToExcel = () => {
    if (chartData.length === 0) {
      alert('No data to export');
      return;
    }

    let exportData = [];
    
    if (parameter === 'overallTemp') {
      const timePoints = chartData[0].x;
      exportData = timePoints.map((time, index) => {
        const row = { Time: time };
        chartData.forEach(dataset => {
          row[dataset.name] = dataset.y[index];
        });
        return row;
      });
    } else {
      exportData = chartData[0].x.map((time, index) => ({
        Time: time,
        [chartData[0].name]: chartData[0].y[index]
      }));
    }

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "ParameterData");
    const fileName = `ParameterTrends_${parameter}_${range}_${new Date().toISOString().slice(0,10)}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  return (
    <>
    <div className="trends">
      <div className="trends-header">
        <h3>Parameter Trends</h3>
        <div className="filters">
          <select value={parameter} onChange={(e) => setParameter(e.target.value)}>
            <option value="overallTemp">Overall Temperature</option>
            <option value="reservoirTemp">Reservoir Temperature</option>
            <option value="pitcherTemp">Pitcher Temperature</option>
            <option value="peltierTemp">Peltier Temperature</option>
          </select>
          
          {!showDateInputs ? (
            <select value={range} onChange={handleRangeChange}>
              <option value="daily">This Day</option>
              <option value="weekly">This Week</option>
              <option value="monthly">This Month</option>
              <option value="yearly">This Year</option>
              <option value="custom">Custom Range</option>
            </select>
          ) : (
            <div className="date-range-inputs">
              <input 
                type="datetime-local" 
                value={startDate} 
                onChange={(e) => setStartDate(e.target.value)} 
              />
              <input 
                type="datetime-local" 
                value={endDate} 
                onChange={(e) => setEndDate(e.target.value)} 
              />
            </div>
          )}
          {showDateInputs && (
            <>
              <button onClick={() => {
                setShowDateInputs(false);
                setRange('6_hours');
              }}>Cancel</button>
            </>
          )}

          <button onClick={() => setIsHistory(true)}>
            <img src={NotifSvg} alt="History" />
          </button>
          <button onClick={() => setIsCHHistory(true)}>
            <img src={NotifSvg} alt="History" />
          </button>
          <button onClick={handleAddClick}>+</button>
          <button onClick={exportToExcel}>Export</button>
        </div>
      </div>

      <div className="chart">
        {loading ? (
          <>
            <p className='load-text'>Loading chart data...</p>
            <div className="loader"><div className="waves"></div></div>
          </>
        ) : chartData.length > 0 ? (
          <Plot
            style={{ width: '100%' }}
            data={chartData.map((item) => ({
              x: item.x,
              y: item.y,
              type: 'scatter',
              mode: 'lines+markers',
              name: item.name,
            }))}
            layout={{
              title: 'Parameter Trends',
              xaxis: { title: 'Time', type: 'category' },
              yaxis: { title: 'Value' },
            }}
          />
        ) : (
          <p>No data available for this range.</p>
        )}
      </div>

      {isHistory && <History close={() => setIsHistory(false)} />}
      {isCHHistory && <CHHistory close={() => setIsCHHistory(false)} />}
      {isAuth && <Auth close={() => setIsAuth(false)} />}
      {isAdd && <Add close={() => setIsAdd(false)} />}
    </div><br/><br/>
    </>
  );
};

export default ParameterTrends;